import type { Deposit } from "@hakili/contracts";
import { etapeCourt } from "@hakili/domain-types";
import { formatDate, formatMoney, t } from "@hakili/i18n";
import { ArrowLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { CapacityBar } from "./eligibilite.js";

/**
 * Round `L-2`, `M2` (`P1-CLW-023`, `P1-TST-002`, `P1-TST-020`; WF-CLI-22, A7, A8):
 * **the buyer compares the offers the banks transmitted, chooses one, and
 * accepts it by a code issued for that act.**
 *
 * ## The screen composes nothing
 *
 * Every figure on a card is the bank's own, answered by `GET /api/v1/me/offers`
 * , the instalment, the rate, the TAEG, the two fees with their own rates, the
 * warranty, the validity, the totals over the duration, the effort, the badges
 * over the selectable set, the adjustments, the difference from the buyer's
 * estimate. The only arithmetic here is a difference between two answered
 * deposits for the amber line (« soit N de plus que la moins exigeante »),
 * which the drawing itself draws as a difference.
 *
 * ## The code for this act
 *
 * The confirmation screen asks the door for a code the moment it opens
 * (`POST …/acceptance-code`), names the channel the door says carried it and
 * the number masked, and accepts with the six digits (`POST …/accept`). A wrong,
 * expired or used code is refused where the gesture is, in the door's own
 * sentence. « Renvoyer le code » follows the record's 60-second cooldown
 * (05 §2.1.9), a drawing in words.
 */

export interface EstimateDifference {
  readonly deltaAmount: number;
  readonly causes: readonly (
    | { readonly kind: "rate"; readonly bankRatePct: string; readonly indicativeRatePct: string }
    | { readonly kind: "bankFee"; readonly ratePct: number }
    | { readonly kind: "warranty"; readonly amount: number }
  )[];
}

/** The door's answer, one shape , `scripts/client-offers-shape.spec.ts` holds it to the api's. */
export interface ClientOfferView {
  readonly id: string;
  readonly dossierId: string;
  readonly bankName: string;
  readonly branchLabel: string | null;
  readonly status: "proposee" | "retenue" | "refusee";
  readonly durationMonths: number;
  readonly nominalRatePct: number;
  readonly taegPct: number;
  readonly instalmentAmount: number;
  readonly insuranceInstalmentAmount: number;
  readonly depositPct: number | null;
  readonly depositAmount: number | null;
  /** `DEPOSIT-D`: the requested and the offered deposit, served and composed by the door; `null` where the price is unknown. */
  readonly deposit: Deposit | null;
  readonly amountVehicle: number;
  readonly amountWarranty: number;
  readonly financesWarranty: boolean;
  readonly warrantyLine: "incluse" | "regleeComptant" | "nonFinancee" | null;
  readonly feeBankAmount: number;
  readonly feeHakiliAmount: number;
  readonly feeBankRatePct: number;
  readonly feeHakiliRatePct: number;
  readonly financesBankFee: boolean;
  readonly financesHakiliFee: boolean;
  readonly amountBorrowed: number;
  readonly totalRepaidAmount: number;
  readonly costOfCreditAmount: number;
  readonly validUntil: string;
  readonly effortPct: number | null;
  readonly netSalaryAmount: number;
  readonly existingCommitmentsAmount: number;
  readonly instalmentPct: number | null;
  readonly resteAVivreAmount: number | null;
  readonly effortBand: "confortable" | "acceptable" | "au-dela" | null;
  readonly exceedsCapacity: boolean;
  /** Round `L-3b`, `M0 (h)`: past its validity on the day the door was asked , unselectable, no badge. */
  readonly expired: boolean;
  readonly badges: readonly ("mensualiteLaPlusBasse" | "laMoinsChereSurLaDuree" | "apportLePlusFaible")[];
  readonly adjustments: readonly ("garantieNonFinancee" | "fraisARegler")[];
  readonly estimateDifference: EstimateDifference | null;
  readonly expectedCircuit: "comite" | "signature" | null;
}

export interface CodeIssued {
  readonly channel: "whatsapp" | "sms";
  readonly phoneMasked: string;
  readonly expiresInSeconds: number;
}

export interface OffresProps {
  readonly offers: readonly ClientOfferView[];
  /** F10: the chargé's agreement is read from a label; `null` while no label says. */
  readonly caseworkerAgreement: "f" | "m" | null;
  readonly askCode: (offerId: string) => Promise<CodeIssued>;
  readonly accept: (offerId: string, code: string) => Promise<{ circuit: "comite" | "signature" | null }>;
  readonly onAccepted: () => void;
  readonly naviguer: (path: string) => void;
}

const DASH = t("common.valeur.tiret");
/** The record's own resend cooldown (05 §2.1.9): sixty seconds. */
export const RESEND_COOLDOWN_SECONDS = 60;

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

const pct = (value: number | string): string => `${String(value).replace(".", ",")} %`;

// The door's wire values (data, keyed as strings) to the catalogue's keys.
const BADGES: ReadonlyMap<string, Parameters<typeof t>[0]> = new Map([
  ["mensualiteLaPlusBasse", "client.offres.badgeMensualite"],
  ["laMoinsChereSurLaDuree", "client.offres.badgeDuree"],
  ["apportLePlusFaible", "client.offres.badgeApport"],
]);

const ADJUSTMENTS: ReadonlyMap<string, Parameters<typeof t>[0]> = new Map([
  ["garantieNonFinancee", "client.offres.ajustementGarantie"],
  ["fraisARegler", "client.offres.ajustementFrais"],
]);

/** A7: the difference line, its causes named from the facts; a dash without an estimate. */
export function differenceLine(difference: EstimateDifference | null): string {
  if (difference === null) return DASH;
  if (difference.deltaAmount === 0) return t("client.offres.memeMensualite");
  const amount = formatMoney(Math.abs(difference.deltaAmount));
  const head = fill(
    difference.deltaAmount < 0 ? "client.offres.moinsQueEstimation" : "client.offres.plusQueEstimation",
    { amount },
  );
  const causes = difference.causes.map((cause) =>
    cause.kind === "rate"
      ? fill("client.offres.causeTaux", {
          bank: pct(cause.bankRatePct),
          indicative: pct(cause.indicativeRatePct),
        })
      : cause.kind === "bankFee"
        ? fill("client.offres.causeFraisBanque", { rate: String(cause.ratePct) })
        : fill("client.offres.causeGarantie", { amount: formatMoney(cause.amount) }),
  );
  return causes.length === 0 ? `${head}.` : `${head} : ${causes.join(", ")}.`;
}

/** WF-CLI-22 step 3's sentence: the adjustments joined as the drawing joins them, the first capitalised. */
function adjustmentsSentence(offer: ClientOfferView): string {
  const parts = offer.adjustments.map((one) => t(ADJUSTMENTS.get(one) ?? one));
  if (parts.length === 0) return "";
  const first = parts[0] ?? "";
  const joined = [first.charAt(0).toUpperCase() + first.slice(1), ...parts.slice(1)].join(
    t("client.offres.etVirgule"),
  );
  return `${joined}.`;
}

/** WF-CLI-22 step 6, with `M0 (i)`: the detail's lines, two fees on two lines, each with its own rate. */
export function detailLines(offer: ClientOfferView): readonly (readonly [string, string])[] {
  const lines: (readonly [string, string])[] = [
    [t("client.offres.lignePrixFinance"), formatMoney(offer.amountVehicle)],
  ];
  if (offer.financesBankFee) {
    lines.push([
      fill("client.offres.ligneFraisBanqueInclus", { rate: String(offer.feeBankRatePct) }),
      formatMoney(offer.feeBankAmount),
    ]);
  }
  if (offer.financesHakiliFee) {
    lines.push([
      fill("client.offres.ligneFraisHakiliInclus", { rate: String(offer.feeHakiliRatePct) }),
      formatMoney(offer.feeHakiliAmount),
    ]);
  }
  if (offer.warrantyLine !== null) {
    lines.push([
      offer.warrantyLine === "incluse"
        ? t("client.offres.ligneGarantieIncluse")
        : offer.warrantyLine === "regleeComptant"
          ? t("client.offres.ligneGarantieComptant")
          : t("client.offres.ligneGarantieNonFinancee"),
      formatMoney(offer.amountWarranty),
    ]);
  }
  lines.push([t("client.offres.ligneMontantEmprunte"), formatMoney(offer.amountBorrowed)]);
  lines.push([
    fill("client.offres.ligneTotalRembourse", { n: String(offer.durationMonths) }),
    formatMoney(offer.totalRepaidAmount),
  ]);
  lines.push([t("client.offres.ligneInterets"), formatMoney(offer.costOfCreditAmount)]);
  if (!offer.financesBankFee) {
    lines.push([t("client.offres.ligneFraisBanqueSignature"), formatMoney(offer.feeBankAmount)]);
  }
  if (!offer.financesHakiliFee) {
    lines.push([t("client.offres.ligneFraisHakiliSignature"), formatMoney(offer.feeHakiliAmount)]);
  }
  return lines;
}

const words = (failure: unknown): string =>
  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");

export function Offres(props: OffresProps): React.JSX.Element {
  const retained = props.offers.find((one) => one.status === "retenue") ?? null;
  const locked = retained !== null;
  const [chosen, setChosen] = useState<string | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const selected = locked ? retained : (props.offers.find((one) => one.id === chosen) ?? null);

  if (confirming && selected !== null && !locked) {
    return (
      <Confirmation
        offer={selected}
        askCode={props.askCode}
        accept={props.accept}
        onBack={() => {
          setConfirming(false);
        }}
        onAccepted={props.onAccepted}
      />
    );
  }

  const selectable = props.offers.filter((one) => !one.exceedsCapacity && !one.expired);
  const deposits = (selectable.length > 0 ? selectable : props.offers)
    .map((one) => one.depositAmount)
    .filter((one): one is number => one !== null);
  const minDeposit = deposits.length === 0 ? null : Math.min(...deposits);
  const count = props.offers.length;
  // `L-5`, `M3`: every card expired , the state's own sentence, no choice asked (A11).
  const allExpired = count > 0 && props.offers.every((one) => one.expired);
  const sentence = locked
    ? t("client.offres.verrouilleSous")
    : allExpired
      ? t("client.offres.toutesExpirees")
      : count === 1
        ? t("client.offres.uneBanqueARepondu")
        : fill("client.offres.banquesOntRepondu", { n: String(count) });

  return (
    <div className="pb-20" data-testid="offres">
      <div className="flex items-start gap-3 px-5 pb-3 pt-5">
        {locked ? (
          <button
            type="button"
            data-testid="offres-retour"
            onClick={() => {
              props.naviguer("/suivi");
            }}
            className="mt-1 text-gray-500"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        ) : null}
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">
            {t("client.offres.vosOffres")}
          </h1>
          <p className="mt-1 text-sm leading-relaxed text-gray-500" data-testid="offres-phrase">
            {sentence}
          </p>
        </div>
      </div>

      <div className="space-y-3 px-5">
        {count === 0 ? (
          <p
            className="rounded-2xl border border-gray-200 bg-white px-4 py-4 text-sm text-gray-500"
            data-testid="offres-aucune"
          >
            {t("client.offres.aucune")}
          </p>
        ) : null}
        {props.offers.map((offer) => {
          const active = locked ? offer.status === "retenue" : chosen === offer.id;
          const blocked = (offer.exceedsCapacity || offer.expired) && !locked;
          return (
            <div
              key={offer.id}
              data-testid={`offre-${offer.id}`}
              data-status={offer.status}
              data-bloquee={blocked ? "true" : "false"}
              className={`w-full rounded-2xl border bg-white px-4 py-4 text-left ${
                blocked ? "border-rose-200 opacity-60" : active ? "border-indigo-400" : "border-gray-200"
              }${locked && !active ? " opacity-50" : ""}`}
            >
              {locked ? (
                <span
                  data-testid="offre-chip-statut"
                  className={`mb-2 inline-block whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${
                    active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {/* The chip's word is the stage's own short label (domain-types', one vocabulary), never a literal. */}
                  {active ? etapeCourt("offre") : t("client.offres.chipRefusee")}
                </span>
              ) : offer.expired ? (
                // `L-4`, M1 (A11): past its validity , the drawing's locked look, the chip beside the
                // bank's name, the figures untouched; never « refusé ».
                <span
                  data-testid="offre-chip-statut"
                  className="mb-2 inline-block rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500"
                >
                  {t("client.offres.chipExpiree")}
                </span>
              ) : null}
              <button
                type="button"
                disabled={blocked || locked}
                onClick={() => {
                  setChosen(offer.id);
                }}
                data-testid="offre-choisir"
                className="flex w-full items-start justify-between gap-2 text-left"
              >
                <div>
                  <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    {offer.bankName}
                    {/* A7: « Offre ferme », a green chip in the badges' family, beside the bank's name ,
                        not on a card past its validity (`L-5`, `M3`: an expired offer is not firm). */}
                    {offer.expired ? null : (
                      <span
                        data-testid="offre-ferme"
                        className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                      >
                        {t("client.offres.offreFerme")}
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">{offer.branchLabel ?? DASH}</p>
                </div>
                <span
                  className={`mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 ${
                    active ? "border-indigo-600 bg-indigo-600" : "border-gray-300"
                  }`}
                />
              </button>

              <p
                className="mt-2 text-2xl font-semibold text-indigo-700 tabular-nums"
                data-testid="offre-mensualite"
              >
                {formatMoney(offer.instalmentAmount)}
                <span className="text-sm font-normal text-gray-500"> {t("client.offres.parMois")}</span>
              </p>
              {/* A7: the difference from his own estimate, its cause named; a dash when the file holds none. */}
              <p className="mt-1 text-xs text-gray-500" data-testid="offre-ecart-estimation">
                {differenceLine(offer.estimateDifference)}
              </p>

              <div className="mt-3 grid grid-cols-3 gap-2">
                {(
                  [
                    [t("client.offres.duree"), `${String(offer.durationMonths)} ${t("client.offres.mois")}`],
                    [t("client.offres.taux"), pct(offer.nominalRatePct)],
                    [t("client.offres.taeg"), pct(offer.taegPct)],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label}>
                    <p className="text-xs text-gray-400">{label}</p>
                    <p className="text-sm font-medium tabular-nums">{value}</p>
                  </div>
                ))}
              </div>

              {offer.adjustments.length > 0 ? (
                <div
                  className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2.5"
                  data-testid="offre-modifiee"
                >
                  <p className="text-xs font-medium text-indigo-700">{t("client.offres.modifieeTitre")}</p>
                  <p className="mt-1 text-xs leading-relaxed text-gray-600">
                    {adjustmentsSentence(offer)} {t("client.offres.modifieePasUnRefus")}
                  </p>
                </div>
              ) : null}

              {offer.expired && !offer.exceedsCapacity ? (
                // `L-3b`, M0 (h): the door's own fact , the validity read on the day it was asked.
                <div
                  className="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2.5"
                  data-testid="offre-expiree"
                >
                  <p className="text-xs font-medium text-rose-600">
                    {fill("client.offres.expireeTitre", { date: formatDate(new Date(offer.validUntil)) })}
                  </p>
                </div>
              ) : null}
              {offer.exceedsCapacity ? (
                <div
                  className="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2.5"
                  data-testid="offre-non-disponible"
                >
                  <p className="text-xs font-medium text-rose-600">{t("client.offres.nonDisponibleTitre")}</p>
                  <p className="mt-1 text-xs leading-relaxed text-gray-600">
                    {fill(
                      props.caseworkerAgreement === "f"
                        ? "client.offres.nonDisponibleDetailChargee"
                        : "client.offres.nonDisponibleDetailCharge",
                      { effort: offer.effortPct === null ? DASH : String(offer.effortPct) },
                    )}
                  </p>
                </div>
              ) : null}

              {!offer.exceedsCapacity && offer.badges.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="offre-badges">
                  {offer.badges.map((badge) => (
                    <span
                      key={badge}
                      className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"
                    >
                      {t(BADGES.get(badge) ?? badge)}
                    </span>
                  ))}
                </div>
              ) : null}

              {/* WF-CLI-22 step 5: the deposit line, amber above the lowest, with the difference and the validity. */}
              <p
                data-testid="offre-apport"
                className={`mt-2.5 text-xs ${
                  offer.depositAmount !== null && minDeposit !== null && offer.depositAmount > minDeposit
                    ? "font-medium text-amber-700"
                    : "text-gray-400"
                }`}
              >
                {fill("client.offres.apportDemande", {
                  amount: offer.depositAmount === null ? DASH : formatMoney(offer.depositAmount),
                })}
                {offer.depositAmount !== null && minDeposit !== null && offer.depositAmount > minDeposit
                  ? fill("client.offres.apportDePlus", {
                      amount: formatMoney(offer.depositAmount - minDeposit),
                    })
                  : ""}{" "}
                {fill("client.offres.valableJusquAu", { date: formatDate(new Date(offer.validUntil)) })}
              </p>

              <button
                type="button"
                data-testid="offre-detail"
                onClick={() => {
                  setDetail(detail === offer.id ? null : offer.id);
                }}
                className="mt-2.5 text-xs font-medium text-indigo-600"
              >
                {detail === offer.id ? t("client.offres.masquerDetail") : t("client.offres.voirDetail")}
              </button>
              {detail === offer.id ? (
                <div className="mt-2.5 border-t border-gray-100 pt-2.5" data-testid="offre-detail-lignes">
                  {detailLines(offer).map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between py-1">
                      <span className="text-xs text-gray-500">{label}</span>
                      <span className="text-xs font-medium tabular-nums">{value}</span>
                    </div>
                  ))}
                  <p className="mt-1.5 text-xs leading-relaxed text-gray-400">
                    {t("client.offres.prixDuCredit")}
                  </p>
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5">
          <p className="text-xs leading-relaxed text-gray-600">{t("client.offres.pedagogie")}</p>
        </div>

        {locked ? null : (
          <button
            type="button"
            data-testid="offres-continuer"
            disabled={selected === null}
            onClick={() => {
              setConfirming(true);
            }}
            className="w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {t("client.offres.continuer")}
          </button>
        )}
      </div>
    </div>
  );
}

function Confirmation(props: {
  readonly offer: ClientOfferView;
  readonly askCode: OffresProps["askCode"];
  readonly accept: OffresProps["accept"];
  readonly onBack: () => void;
  readonly onAccepted: () => void;
}): React.JSX.Element {
  const { offer } = props;
  const [issued, setIssued] = useState<CodeIssued | null>(null);
  const [issueWord, setIssueWord] = useState<string | null>(null);
  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const asked = useRef(false);

  const ask = (): void => {
    setIssueWord(null);
    void (async () => {
      try {
        const answer = await props.askCode(offer.id);
        setIssued(answer);
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } catch (failure) {
        setIssueWord(words(failure));
      }
    })();
  };
  // The code is asked the moment the screen opens: the drawing's sentence names a code already sent.
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    ask();
  });
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => {
      setCooldown(cooldown - 1);
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [cooldown]);

  const complete = digits.every((one) => one !== "");
  const submit = (): void => {
    setBusy(true);
    setRefusal(null);
    void (async () => {
      try {
        await props.accept(offer.id, digits.join(""));
        props.onAccepted();
      } catch (failure) {
        setRefusal(words(failure));
      } finally {
        setBusy(false);
      }
    })();
  };

  const channelWord =
    issued === null
      ? DASH
      : issued.channel === "sms"
        ? t("client.offres.canalSms")
        : t("client.offres.canalWhatsapp");
  const warrantyDecisionAhead =
    offer.amountWarranty > 0 && !offer.financesWarranty && offer.warrantyLine !== "regleeComptant";

  return (
    <div className="pb-20" data-testid="offre-confirmation">
      <div className="flex items-center gap-3 px-5 pb-3 pt-5">
        <button
          type="button"
          data-testid="confirmation-retour"
          onClick={props.onBack}
          className="text-gray-500"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="text-lg font-semibold tracking-tight text-gray-900">
          {t("client.offres.confirmerTitre")}
        </h1>
      </div>

      <div className="px-5">
        <div
          className="rounded-2xl border border-gray-200 bg-white px-4 py-4"
          data-testid="confirmation-recap"
        >
          <p className="text-sm font-semibold text-gray-900">{offer.bankName}</p>
          <p className="mt-1 text-2xl font-semibold text-indigo-700 tabular-nums">
            {formatMoney(offer.instalmentAmount)} {t("client.offres.parMois")}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {String(offer.durationMonths)} {t("client.offres.mois")} · {t("client.offres.taeg")}{" "}
            {pct(offer.taegPct)} ·{" "}
            {fill("client.offres.apportCourt", {
              amount: offer.depositAmount === null ? DASH : formatMoney(offer.depositAmount),
            })}
          </p>
          <div className="mt-3 border-t border-gray-100 pt-3">
            {detailLines(offer).map(([label, value]) => (
              <div key={label} className="flex items-center justify-between py-1">
                <span className="text-xs text-gray-500">{label}</span>
                <span className="text-xs font-medium tabular-nums">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {offer.adjustments.length > 0 ? (
          <div
            className="mt-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3.5"
            data-testid="confirmation-ajustements"
          >
            <p className="text-xs font-medium text-indigo-700">{t("client.offres.modifieeTitre")}</p>
            <p className="mt-1 text-xs leading-relaxed text-gray-600">
              {adjustmentsSentence(offer)}
              {warrantyDecisionAhead ? ` ${t("client.offres.garantieApresAcceptation")}` : ""}
            </p>
          </div>
        ) : null}

        <div
          className="mt-3 rounded-2xl border border-gray-200 bg-white px-4 py-4"
          data-testid="confirmation-capacite"
        >
          <p className="mb-1 text-sm font-semibold text-gray-900">{t("client.offres.representeTitre")}</p>
          {/*
           * R-B (round `L-2`): the drawing's bar is row 61's , one component, the
           * three segments and the band's sentence , drawn from the buyer's OWN
           * facts his door answers beside the effort. Without a salary the bar
           * refuses to render and the drawing's sentence stands in its place.
           */}
          {offer.effortPct === null || offer.effortBand === null ? (
            <p className="text-xs leading-relaxed text-gray-500">
              {t("client.offres.representeSansSalaire")}
            </p>
          ) : (
            <>
              <p className="text-xs leading-relaxed text-gray-500">{t("client.offres.representeDetail")}</p>
              <CapacityBar
                effortPct={offer.effortPct}
                instalmentPct={offer.instalmentPct}
                band={offer.effortBand}
                netSalaryAmount={offer.netSalaryAmount}
                existingLoanInstalmentAmount={offer.existingCommitmentsAmount}
                instalmentAmount={offer.instalmentAmount}
                resteAVivreAmount={offer.resteAVivreAmount}
              />
            </>
          )}
        </div>

        {/* A8: the amber warning follows the bank's own circuit , comité above its threshold, direct inside it. */}
        <div
          className="mt-3 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3.5"
          data-testid="confirmation-avertissement"
        >
          <p className="text-xs leading-relaxed text-gray-700">
            {t("client.offres.avertissementRefus")}{" "}
            {fill(
              offer.expectedCircuit === "comite"
                ? "client.offres.avertissementComite"
                : offer.expectedCircuit === "signature"
                  ? "client.offres.avertissementDirect"
                  : "client.offres.avertissementSansSeuil",
              { bank: offer.bankName },
            )}
          </p>
        </div>

        <p className="mb-2 mt-5 text-sm leading-relaxed text-gray-600" data-testid="confirmation-code-phrase">
          {issued === null && issueWord !== null
            ? issueWord
            : fill("client.offres.codePhrase", { channel: channelWord, phone: issued?.phoneMasked ?? DASH })}
        </p>
        <div className="flex items-center gap-2" data-testid="confirmation-code">
          {digits.map((digit, index) => (
            <input
              key={`chiffre-${String(index)}`}
              ref={(element) => {
                inputs.current[index] = element;
              }}
              value={digit}
              maxLength={1}
              inputMode="numeric"
              aria-label={fill("client.offres.chiffre", { n: String(index + 1) })}
              onChange={(event) => {
                const value = event.target.value.replace(/\D/gu, "").slice(-1);
                const next = [...digits];
                next[index] = value;
                setDigits(next);
                if (value !== "" && index < 5) inputs.current[index + 1]?.focus();
              }}
              className="h-14 w-full rounded-2xl border border-gray-200 text-center text-xl font-semibold tabular-nums outline-none focus:border-indigo-400"
            />
          ))}
        </div>
        <button
          type="button"
          data-testid="confirmation-renvoyer"
          disabled={cooldown > 0}
          title={cooldown > 0 ? fill("client.offres.renvoyerDans", { s: String(cooldown) }) : undefined}
          onClick={ask}
          className="mt-3 text-sm font-medium text-indigo-600 disabled:opacity-40"
        >
          {cooldown > 0
            ? fill("client.offres.renvoyerDans", { s: String(cooldown) })
            : t("client.offres.renvoyer")}
        </button>

        <button
          type="button"
          data-testid="confirmation-accepter"
          disabled={!complete || busy || issued === null}
          onClick={submit}
          className="mt-5 w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-40"
        >
          {t("client.offres.accepter")}
        </button>
        {refusal === null ? null : (
          <p role="alert" data-testid="confirmation-refus" className="mt-2 text-sm text-rose-600">
            {refusal}
          </p>
        )}
        <p className="mt-3 text-center text-xs leading-relaxed text-gray-400">{t("client.offres.preuve")}</p>
      </div>
    </div>
  );
}
